import { useState } from 'react';
import { ChevronDown, ArrowUpAZ, ArrowDownZA, Filter as FilterIcon, ArrowLeft, Sigma, Palette, ListOrdered, Group, Pencil } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { ColumnDef, DateFilter, DateFilterMode, SortState } from './types';
import { GroupMode } from './savedViews';
import { RelativeDateInput } from './RelativeDateInput';

export type SortAction = 'replace-asc' | 'replace-desc' | 'append-asc' | 'append-desc' | 'remove';

interface ColumnHeaderMenuProps {
  column: ColumnDef;
  sort: SortState;
  filterValues: Set<string> | undefined;
  dateFilter: DateFilter | undefined;
  uniqueValues: string[];
  showTotal: boolean;
  hasFormat: boolean;
  isGroupedBy: boolean;
  groupMode: GroupMode | undefined;
  onSort: (action: SortAction) => void;
  onFilterChange: (values: Set<string>) => void;
  onDateFilterChange: (df: DateFilter | null) => void;
  onToggleTotal: () => void;
  onOpenFormat: () => void;
  onToggleGroupBy: () => void;
  onSetGroupMode: (mode: GroupMode) => void;
  onRename: (label: string | undefined) => void;
}

export function ColumnHeaderMenu({
  column,
  sort,
  filterValues,
  dateFilter,
  uniqueValues,
  showTotal,
  hasFormat,
  isGroupedBy,
  groupMode,
  onSort,
  onFilterChange,
  onDateFilterChange,
  onToggleTotal,
  onOpenFormat,
  onToggleGroupBy,
  onSetGroupMode,
  onRename,
}: ColumnHeaderMenuProps) {
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState<'menu' | 'filter' | 'rename'>('menu');
  const [draftLabel, setDraftLabel] = useState('');

  const sortIndex = sort.findIndex((s) => s.columnId === column.id);
  const isSortedHere = sortIndex >= 0;
  const mySortDir = isSortedHere ? sort[sortIndex].direction : null;
  const isDate = column.dataType === 'date';
  const hasFilter = (filterValues?.size ?? 0) > 0 || !!dateFilter;
  const hasExistingSort = sort.length > 0;

  const doAction = (action: SortAction) => {
    onSort(action);
    setOpen(false);
  };

  const toggleFilterValue = (value: string) => {
    const next = new Set(filterValues ?? []);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onFilterChange(next);
  };

  const clearFilter = () => {
    onFilterChange(new Set());
    if (isDate) onDateFilterChange(null);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setPanel('menu');
      }}
    >
      <PopoverTrigger asChild>
        <button
          className={cn(
            'group inline-flex items-center gap-1 min-w-0 px-1.5 py-2 w-full text-left uppercase text-xs font-semibold tracking-wider text-slate-600 hover:bg-slate-50 transition-colors',
            isSortedHere && 'text-slate-900'
          )}
        >
          {hasFilter && <FilterIcon className="w-3 h-3 text-blue-500 fill-blue-500 shrink-0" />}
          {hasFormat && <Palette className="w-3 h-3 text-slate-500 shrink-0" />}
          {isGroupedBy && <Group className="w-3 h-3 text-amber-500 shrink-0" />}
          <span className="truncate min-w-0">{column.label}</span>
          {isSortedHere && (
            <span className="flex items-center gap-0.5 text-slate-900">
              {mySortDir === 'asc' ? (
                <ArrowUpAZ className="w-3 h-3" />
              ) : (
                <ArrowDownZA className="w-3 h-3" />
              )}
              {sort.length > 1 && (
                <span className="text-[10px] leading-none font-bold">{sortIndex + 1}</span>
              )}
            </span>
          )}
          <ChevronDown
            className={cn(
              'w-3 h-3 text-slate-400 transition-transform ml-auto shrink-0',
              open && 'rotate-180',
              isSortedHere && 'text-slate-900'
            )}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={4} className="w-60 p-1">
        {panel === 'menu' ? (
          <div className="py-1">
            <MenuItem
              icon={<ArrowUpAZ className="w-4 h-4" />}
              label="Sort A-Z"
              active={isSortedHere && sort.length === 1 && mySortDir === 'asc'}
              onClick={() => doAction('replace-asc')}
            />
            <MenuItem
              icon={<ArrowDownZA className="w-4 h-4" />}
              label="Sort Z-A"
              active={isSortedHere && sort.length === 1 && mySortDir === 'desc'}
              onClick={() => doAction('replace-desc')}
            />
            {hasExistingSort && !isSortedHere && (
              <>
                <MenuItem
                  icon={<ListOrdered className="w-4 h-4" />}
                  label="Add to sort ↑"
                  onClick={() => doAction('append-asc')}
                />
                <MenuItem
                  icon={<ListOrdered className="w-4 h-4" />}
                  label="Add to sort ↓"
                  onClick={() => doAction('append-desc')}
                />
              </>
            )}
            {isSortedHere && sort.length > 1 && (
              <MenuItem
                icon={<ListOrdered className="w-4 h-4" />}
                label="Remove from sort"
                onClick={() => doAction('remove')}
              />
            )}
            <div className="my-1 border-t border-slate-100" />
            <MenuItem
              icon={<FilterIcon className="w-4 h-4" />}
              label="Filter"
              active={hasFilter}
              onClick={() => setPanel('filter')}
            />
            <MenuItem
              icon={<Sigma className="w-4 h-4" />}
              label={showTotal ? 'Hide Total' : 'Show Total'}
              active={showTotal}
              onClick={() => {
                onToggleTotal();
                setOpen(false);
              }}
            />
            <MenuItem
              icon={<Group className="w-4 h-4" />}
              label={isGroupedBy ? 'Stop Grouping' : 'Group by This'}
              active={isGroupedBy}
              onClick={() => {
                onToggleGroupBy();
                setOpen(false);
              }}
            />
            {isGroupedBy && (
              <MenuItem
                icon={<Group className="w-4 h-4" />}
                label={groupMode === 'summary' ? 'Show Row Details' : 'Summary Only'}
                active={groupMode === 'summary'}
                onClick={() => {
                  onSetGroupMode(groupMode === 'summary' ? 'detail' : 'summary');
                  setOpen(false);
                }}
              />
            )}
            <MenuItem
              icon={<Pencil className="w-4 h-4" />}
              label="Rename column"
              onClick={() => {
                setDraftLabel(column.label);
                setPanel('rename');
              }}
            />
            <MenuItem
              icon={<Palette className="w-4 h-4" />}
              label="Conditional Format"
              active={hasFormat}
              onClick={() => {
                onOpenFormat();
                setOpen(false);
              }}
            />
          </div>
        ) : panel === 'rename' ? (
          <div className="flex flex-col">
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
              <button
                className="flex items-center gap-1.5 text-sm font-semibold text-slate-900 hover:text-slate-600"
                onClick={() => setPanel('menu')}
              >
                <ArrowLeft className="w-4 h-4" />
                Rename
              </button>
              <button
                className="text-sm font-medium text-blue-600 hover:text-blue-700"
                onClick={() => {
                  onRename(undefined);
                  setOpen(false);
                }}
              >
                Reset
              </button>
            </div>
            <div className="p-2 space-y-2">
              <Input
                value={draftLabel}
                onChange={(e) => setDraftLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const trimmed = draftLabel.trim();
                    onRename(trimmed ? trimmed : undefined);
                    setOpen(false);
                  }
                  if (e.key === 'Escape') {
                    setPanel('menu');
                  }
                }}
                placeholder={column.label}
                autoFocus
                className="h-8 text-sm"
              />
              <button
                onClick={() => {
                  const trimmed = draftLabel.trim();
                  onRename(trimmed ? trimmed : undefined);
                  setOpen(false);
                }}
                className="w-full h-8 rounded-md bg-slate-900 text-white text-xs font-medium hover:bg-slate-800"
              >
                Save
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col max-h-80">
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
              <button
                className="flex items-center gap-1.5 text-sm font-semibold text-slate-900 hover:text-slate-600"
                onClick={() => setPanel('menu')}
              >
                <ArrowLeft className="w-4 h-4" />
                Filter by:
              </button>
              <button
                className="text-sm font-medium text-blue-600 hover:text-blue-700"
                onClick={clearFilter}
              >
                Clear
              </button>
            </div>
            <div className="overflow-y-auto py-1.5">
              {isDate && (
                <DateFilterEditor
                  value={dateFilter}
                  onChange={onDateFilterChange}
                />
              )}
              {uniqueValues.length === 0 ? (
                !isDate && (
                  <div className="px-3 py-6 text-sm text-slate-400 text-center">No values</div>
                )
              ) : (
                uniqueValues.map((value) => {
                  const checked = filterValues?.has(value) ?? false;
                  return (
                    <label
                      key={value}
                      className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-slate-50 rounded-sm"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleFilterValue(value)}
                      />
                      <span className="text-sm text-slate-900 font-medium truncate">
                        {value || <span className="text-slate-400 italic">(empty)</span>}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface MenuItemProps {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick: () => void;
}

interface DateFilterEditorProps {
  value: DateFilter | undefined;
  onChange: (df: DateFilter | null) => void;
}

const MODE_LABELS: Record<DateFilterMode, string> = {
  eq: 'On',
  neq: 'Not on',
  gt: 'After',
  lt: 'Before',
  gte: 'On or after',
  lte: 'On or before',
  between: 'Between',
};

function DateFilterEditor({ value, onChange }: DateFilterEditorProps) {
  const mode: DateFilterMode = value?.mode ?? 'gte';
  const v1 = value?.value ?? '';
  const v2 = value?.value2 ?? '';

  const setMode = (m: DateFilterMode) =>
    onChange({ mode: m, value: v1, value2: m === 'between' ? v2 : undefined });

  const setV1 = (s: string) => {
    if (!s && (mode !== 'between' || !v2)) {
      onChange(null);
      return;
    }
    onChange({ mode, value: s, value2: mode === 'between' ? v2 : undefined });
  };
  const setV2 = (s: string) => {
    if (!v1 && !s) {
      onChange(null);
      return;
    }
    onChange({ mode, value: v1, value2: s });
  };

  return (
    <div className="px-2 pt-2 pb-1 space-y-1.5 border-b border-slate-100 mb-1">
      <Select value={mode} onValueChange={(m) => setMode(m as DateFilterMode)}>
        <SelectTrigger className="h-8 text-xs">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {(Object.keys(MODE_LABELS) as DateFilterMode[]).map((m) => (
            <SelectItem key={m} value={m} className="text-xs">
              {MODE_LABELS[m]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {mode === 'between' ? (
        <div className="space-y-1">
          <RelativeDateInput value={v1} onChange={setV1} size="sm" />
          <RelativeDateInput value={v2} onChange={setV2} size="sm" />
        </div>
      ) : (
        <RelativeDateInput value={v1} onChange={setV1} size="sm" />
      )}
    </div>
  );
}

function MenuItem({ icon, label, active, onClick }: MenuItemProps) {
  return (
    <button
      className={cn(
        'w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-slate-900 hover:bg-slate-50 rounded-sm transition-colors',
        active && 'ring-1 ring-blue-300 bg-blue-50/50'
      )}
      onClick={onClick}
    >
      <span className="text-slate-500">{icon}</span>
      {label}
    </button>
  );
}
